// components/public/PublicUi.jsx
import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import {
  ApartmentOutlined,
  BuildOutlined,
  CleaningServicesOutlined,
  LayersOutlined,
  HotelOutlined,
  ArrowForwardRounded,
  CheckRounded,
  PhotoLibraryOutlined,
  GroupsOutlined,
  AssignmentTurnedInOutlined,
  FactCheckOutlined,
  BusinessCenterOutlined,
} from '@mui/icons-material';
import { BRAND, SERVICE_ICON_KEYS, WORKFLOW_STEPS } from './publicSiteData';
import { PUBLIC_BASE_URL, PUBLIC_RELEASE_ID } from './publicSiteRegistry.mjs';

const iconMap = {
  home: ApartmentOutlined,
  build: BuildOutlined,
  clean: CleaningServicesOutlined,
  layers: LayersOutlined,
  hotel: HotelOutlined,
};

export function ServiceIcon({ slug, className = '' }) {
  const Icon = iconMap[SERVICE_ICON_KEYS[slug]] || BusinessCenterOutlined;
  return <Icon className={className} aria-hidden="true" />;
}

export function ArrowIcon() {
  return <ArrowForwardRounded aria-hidden="true" />;
}

function normalizePath(path = '/') {
  return path === '/' ? '/' : `/${String(path).replace(/^\/+|\/+$/g, '')}`;
}

function breadcrumbSchema(items = []) {
  const itemListElement = items
    .filter((item) => item?.label)
    .map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.to
        ? `${PUBLIC_BASE_URL}${normalizePath(item.to)}`
        : undefined,
    }));

  if (!itemListElement.length) return null;

  return {
    '@type': 'BreadcrumbList',
    itemListElement,
  };
}

export function PublicSeo({
  page,
  title,
  description,
  path = '/',
  pageType = 'WebPage',
  serviceName,
  serviceType,
  breadcrumbs = [],
  faqItems = [],
  robots = 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1',
  includeCanonical = true,
  includeStructuredData = true,
}) {
  const resolvedTitle = page?.title || title;
  const resolvedDescription = page?.description || description;
  const resolvedPath = normalizePath(page?.path || path);
  const resolvedPageType = page?.schemaType || pageType;
  const canonical = `${PUBLIC_BASE_URL}${resolvedPath}`;

  const graph = [
    {
      '@type': 'Organization',
      '@id': `${PUBLIC_BASE_URL}/#organization`,
      name: BRAND.name,
      legalName: BRAND.legalName,
      url: `${PUBLIC_BASE_URL}/`,
      logo: `${PUBLIC_BASE_URL}/Tasker_Sys_logo.png`,
      telephone: '+17183742603',
      email: BRAND.email,
      areaServed: {
        '@type': 'City',
        name: 'New York City',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+17183742603',
        email: BRAND.email,
        contactType: 'customer service',
        areaServed: 'US-NY',
        availableLanguage: ['English'],
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: '330 East 49th St',
        addressLocality: 'New York',
        addressRegion: 'NY',
        postalCode: '10017',
        addressCountry: 'US',
      },
    },
    {
      '@type': 'WebSite',
      '@id': `${PUBLIC_BASE_URL}/#website`,
      name: BRAND.name,
      url: `${PUBLIC_BASE_URL}/`,
      publisher: { '@id': `${PUBLIC_BASE_URL}/#organization` },
      inLanguage: 'en-US',
    },
    {
      '@type': resolvedPageType,
      '@id': `${canonical}#webpage`,
      url: canonical,
      name: resolvedTitle,
      description: resolvedDescription,
      isPartOf: { '@id': `${PUBLIC_BASE_URL}/#website` },
      about: { '@id': `${PUBLIC_BASE_URL}/#organization` },
      inLanguage: 'en-US',
    },
  ];

  const crumbs = breadcrumbSchema(breadcrumbs);
  if (crumbs) graph.push(crumbs);

  if (serviceName) {
    graph.push({
      '@type': 'Service',
      '@id': `${canonical}#service`,
      name: serviceName,
      serviceType: serviceType || serviceName,
      description: resolvedDescription,
      provider: { '@id': `${PUBLIC_BASE_URL}/#organization` },
      areaServed: {
        '@type': 'City',
        name: 'New York City',
      },
      url: canonical,
      mainEntityOfPage: { '@id': `${canonical}#webpage` },
    });
  }

  if (Array.isArray(faqItems) && faqItems.length) {
    graph.push({
      '@type': 'FAQPage',
      '@id': `${canonical}#faq`,
      mainEntity: faqItems
        .filter((item) => item?.q && item?.a)
        .map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.a,
          },
        })),
    });
  }

  return (
    <Helmet>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <meta name="taskersys-public-release" content={PUBLIC_RELEASE_ID} />
      <meta name="robots" content={robots} />
      {includeCanonical ? <link rel="canonical" href={canonical} /> : null}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="TaskerSys" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      {includeCanonical ? <meta property="og:url" content={canonical} /> : null}
      <meta property="og:image" content={`${PUBLIC_BASE_URL}/Tasker_Sys_logo.png`} />
      <meta property="og:image:alt" content="TaskerSys logo" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={`${PUBLIC_BASE_URL}/Tasker_Sys_logo.png`} />
      {includeStructuredData ? (
        <script type="application/ld+json">
          {JSON.stringify({ '@context': 'https://schema.org', '@graph': graph })}
        </script>
      ) : null}
    </Helmet>
  );
}

export function Breadcrumbs({ items }) {
  return (
    <nav className="ts-breadcrumbs ts-container" aria-label="Breadcrumb">
      <ol>
        {items.map((item, index) => (
          <li key={`${item.label}-${index}`}>
            {item.to ? (
              <Link to={item.to}>{item.label}</Link>
            ) : (
              <span aria-current="page">{item.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function SectionHeading({ eyebrow, title, children, linkTo, linkLabel }) {
  return (
    <div className="ts-section-head">
      <div>
        {eyebrow ? <p className="ts-eyebrow">{eyebrow}</p> : null}
        <h2>{title}</h2>
        {children ? <div className="ts-section-copy">{children}</div> : null}
      </div>
      {linkTo && linkLabel ? (
        <Link className="ts-text-link" to={linkTo}>
          {linkLabel}
          <ArrowIcon />
        </Link>
      ) : null}
    </div>
  );
}

export function CheckList({ items, className = '' }) {
  return (
    <ul className={`ts-checklist ${className}`.trim()}>
      {items.map((item) => (
        <li key={item}>
          <CheckRounded aria-hidden="true" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function WorkflowCard() {
  return (
    <aside className="ts-workflow" aria-label="A typical workflow from request to closeout">
      <div className="ts-workflow-heading">
        <span className="ts-eyebrow">HOW THE WORK CONNECTS</span>
        <span className="ts-live-dot" aria-hidden="true" />
      </div>
      <p className="ts-workflow-title">
        One scope.
        <br />
        A clear path forward.
      </p>
      <ol>
        {WORKFLOW_STEPS.map((step) => (
          <li key={step.number}>
            <span className="ts-step-number">{step.number}</span>
            <div>
              <strong>{step.title}</strong>
              <span>{step.text}</span>
            </div>
            <ArrowForwardRounded aria-hidden="true" />
          </li>
        ))}
      </ol>
      <div className="ts-workflow-foot">
        Field services <span aria-hidden="true">↔</span> Operations platform
      </div>
    </aside>
  );
}

export function PublicHero({
  page,
  eyebrow,
  title,
  lead,
  primary,
  secondary,
  note,
  children,
  compact = false,
}) {
  const resolvedEyebrow = page?.eyebrow || eyebrow;
  const resolvedTitle = page?.heading || title;
  const resolvedLead = page?.lead || lead;
  const resolvedPrimary = primary ?? page?.primaryAction;
  const resolvedSecondary = secondary ?? page?.secondaryAction;
  const resolvedNote = note ?? page?.heroNote;

  return (
    <section className={`ts-hero ${compact ? 'ts-hero--compact' : 'ts-hero--home'}`}>
      <div className={`ts-container ${children ? 'ts-hero-grid' : ''}`}>
        <div className="ts-hero-copy">
          {resolvedEyebrow ? <p className="ts-eyebrow">{resolvedEyebrow}</p> : null}
          <h1>{resolvedTitle}</h1>
          <p className="ts-lead">{resolvedLead}</p>
          {resolvedPrimary || resolvedSecondary ? (
            <div className="ts-actions">
              {resolvedPrimary ? (
                <Link className="ts-button ts-button--primary" to={resolvedPrimary.to}>
                  {resolvedPrimary.label}
                  <ArrowIcon />
                </Link>
              ) : null}
              {resolvedSecondary ? (
                <Link className="ts-button ts-button--outline" to={resolvedSecondary.to}>
                  {resolvedSecondary.label}
                  <ArrowIcon />
                </Link>
              ) : null}
            </div>
          ) : null}
          {resolvedNote ? <p className="ts-hero-note">{resolvedNote}</p> : null}
        </div>
        {children}
      </div>
    </section>
  );
}

export function CtaBand({
  eyebrow = 'LET’S TALK ABOUT THE WORK',
  title = 'Start with one scope. Build from there.',
  text = 'Tell us about the property, the work required, and your target timing. We can discuss the right next step.',
  buttonLabel = 'Tell us what you need',
  buttonTo = '/contact',
}) {
  return (
    <section className="ts-contact-band">
      <div className="ts-container">
        <div>
          <p className="ts-eyebrow">{eyebrow}</p>
          <h2>{title}</h2>
          <p>{text}</p>
        </div>
        <div className="ts-band-actions">
          <Link className="ts-button ts-button--white" to={buttonTo}>
            {buttonLabel}
            <ArrowIcon />
          </Link>
          <a href={BRAND.phoneHref}>Or call {BRAND.phoneDisplay}</a>
        </div>
      </div>
    </section>
  );
}

export const publicFeatureIcons = {
  projects: PhotoLibraryOutlined,
  roles: GroupsOutlined,
  closeout: AssignmentTurnedInOutlined,
  checklist: FactCheckOutlined,
};

